import { Component, Injector, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { State } from '@progress/kendo-data-query';
import { forkJoin } from 'rxjs';
import { MainFormBase } from '../../../../../components/common/main-form-base/main-form-base';
import { PolluxFormConfig } from '../../../../../components/common/pollux-form-config/pollux-form-config';
import { PolluxGridConfig } from '../../../../../components/common/pollux-grid-config/pollux-grid-config';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { ValuationEventCopyService } from '../../../services/valuation-event-copy.service';
import { ValuationEventPropertiesService } from '../../../services/valuation-event-properties.service';
import { ValuationEvenRoleManagementService } from '../../../services/valuation-event-role-management.service';
import { copyValuationEventFormConfig } from '../../forms/copy-valuation-event-form/copy-valuation-event-form-config';
import { valuationEventPropertiesGridColumns } from './valuation-event-properties-grid-config';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';

/**
 * Copy VE dialog
 */
@Component({
  selector: 'copy-valuation-portfolio-dialog',
  templateUrl: './copy-valuation-portfolio-dialog.component.html',
  styleUrls: ['./copy-valuation-portfolio-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CopyValuationPortfolioDialogComponent extends MainFormBase<any> implements OnInit {
  /**
   * Window title
   */
  windowTitle = '';

  /**
   * Page
   */
  page = 1;

  /**
   * Copy VE data
   */
  copyValuationPortfolioData = null;

  /**
   * Copying right now?
   */
  copying = false;

  /**
   * Original VE (source) data
   */
  originalValuationPortfolioData = null;

  /**
   * Copied OID
   */
  copiedOid = -1;

  /**
   * Period of valuation
   */
  periodOfValuation = null;

  /**
   * Available roles
   */
  availableRoles = [];

  /**
   * Available users
   */
  availableUsers = [];

  /**
   * Period of valuation
   */
  PeriodOfValuationAndValuationDate = null;

  /**
   * Enable save?
   */
  enableSaveOnPortfolioData = false;

  /**
   * Valuation date invalid?
   */
  valuationDateInvalid = true;

  /**
   * Datepicker for the valuation date
   */
  datePickerValuationDate = null;

  /**
   * Selected VE to copy
   */
  selectedPortfolioToCopy = null;

  /**
   * Property data
   */
  propertyData = null;

  /**
   * Selected properties
   */
  selectedProperties = [];

  /**
   * Dialog opened?
   */
  isDialogOpened = false;

  /**
   * Copy confirmed?
   */
  isCopyConfirmed = false;

  /**
   * Check all?
   */
  checkAllChecked = false;

  /**
   * Available valuation periods
   */
  availableValuationPeriods = [];

  /**
 * Pager config
 */
  protected pagerConfig: any = {
    pageSize: 50,
    sizes: [10, 25, 50, 100],
    numericButtonsCount: 0, //how many buttons for pages
  }

  /**
 * The current grid state
 */
  public state: State = {
    skip: 0,
    take: this.pagerConfig.pageSize,
    group: [],
    filter: { filters: [], logic: "and" },
    sort: []
  };

  /**
   * Constructor
   * @param injector Injector
   * @param valEventPropertiesService ValuationEventPropertiesService
   * @param valEventRoleMgmtService ValuationEvenRoleManagementService
   * @param valEventCopyService ValuationEventCopyService
   * @param _translate TranslateService
   */
  constructor(protected injector: Injector,
    private valEventPropertiesService: ValuationEventPropertiesService,
    private valEventRoleMgmtService: ValuationEvenRoleManagementService,
    private valEventCopyService: ValuationEventCopyService,
    private _translate: TranslateService) {
    super(injector);
  }

  /**
   * Periods of valuation
   */
  availablePeriodOfValuationAndValuationDateDataSource = [];

  /**
   * Workspace reference
   */
  public get workspaceReference(): WorkspaceBase {
    return null;
  }

  /**
   * The form config
   */
  protected get formConfig(): PolluxFormConfig[] {
    return copyValuationEventFormConfig;
  }

  /**
   * Load single VE endpoint
   * @param oid
   * @returns
   */
  protected getLoadSingleEndpoint(oid: any): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios?oid=${oid}`);
  }

  /**
   * Save VE endpoint
   * @returns
   */
  protected getSaveSingleEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios/copy`);
  }

  /**
   * Sets the default retriever data
   * @param data
   */
  protected setDefaultRetrieverData(data: any) {
    //Nothing
  }

  /**
   * The required reflists
   */
  protected get requiredReflists(): string[] {
    return [
      'ref_valuation_project_periodofvaluation'
    ];
  }

  /**
   * The required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return [];
  }

  /**
   * Close
   */
  public close(): void {
    throw new Error('Method not implemented.');
  }

  /**
   * Post-process data
   * @param data
   * @returns
   */
  protected postProcessData(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        this.copyValuationPortfolioData = data;
        this.originalValuationPortfolioData = data;
        this.copyValuationPortfolioData.PeriodOfValuationID = null;
        resolve(data);

      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Handle validation errors
   * @param data
   */
  public handleValidationError(data: any): void {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      hideConfirmButton: true,
      cancel: () => {
        //Nothing
      }
    }, 'fillRequiredFieldsDialog');
  }

  /**
   * Evaluate the save result
   * @param result
   * @returns
   */
  public evaluateSaveResult(result: any): boolean {
    this.eventBus.broadcastEvent('valuationProjectPortfolioItemSaved', { m_Item1: true, m_Item2: this.formData.controls.Oid.value });
    this.eventBus.broadcastEvent('valuationProjectPortfolioItemCopied', this.formData.value);
    this.isDialogOpened = false;

    return result?.Oid;
  }

  /**
   * Any pre-saving operations
   * @param data
   * @returns
   */
  public handlePreSave(data: any): Promise<boolean> {
    return this.noPreSave();
  }

  /**
   * Handle aborted saving
   * @param data
   */
  public handleSaveAborted(data: any): void {
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * Actions after saving
   * @param saveResult
   */
  protected handlePostSave(saveResult: any): void {
    this.eventBus.broadcastEvent('valuationProjectPortfolioItemSaved', { m_Item1: true, m_Item2: saveResult.Oid });
    this.eventBus.broadcastEvent('valuationProjectPortfolioItemCopied', this.formData.value);
    this.isDialogOpened = false;
  }

  /**
   *
   * Handle save errors
   * @param error
   */
  protected handleSaveError(error: any): void {
    alert('An error occurred while saving your data!');
    console.error(error);
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.subscribeToOpenCopyPortfolioAssistant();
  }

  /**
   * Cancel
   */
  cancel() {
    this.isDialogOpened = false;
  }

  /**
   * Close the dialog
   */
  closeDialog() {
    this.isDialogOpened = false;
  }

  /**
   * The columns
   */
  public get columns(): PolluxGridConfig[] {
    return valuationEventPropertiesGridColumns;
  }

  /**
   * Enable the next button
   * @param value
   */
  enableNextOnPortfolioData(value: boolean) {
    this.enableSaveOnPortfolioData = value;
  }

  /**
   * Subscribe to the openCopyValuationPortfolioAssistant event
   */
  subscribeToOpenCopyPortfolioAssistant() {
    this.eventBus.subscribeEvent('openCopyValuationPortfolioAssistant', (item, _options) => {
      this.copying = false;
      this.enableSaveOnPortfolioData = false;
      this.valuationDateInvalid = true;
      this.page = 1;
      this.selectedProperties = [];
      this.selectedPortfolioToCopy = item;
      this.copyValuationPortfolioData = ObjectUtil.deepCopy(item);
      this.copyValuationPortfolioData.ValuationDateDescription = this.copyValuationPortfolioData.ValuationDate;
      this.originalValuationPortfolioData = ObjectUtil.deepCopy(this.copyValuationPortfolioData);
      this.currentOid = this.copyValuationPortfolioData.Oid;

      this.init().then(() => {
        this.formData.patchValue(this.copyValuationPortfolioData);
        this.formData.controls.Oid.setValue(-1);
      });

      this.getData(item).subscribe(data => {
        const properties = data[0].details as any[];
        const users = data[1];
        const roles = data[2];
        this.availableValuationPeriods = data[4];

        for (const property of properties) {
          const OccursInValuationPortfolios = JSON.parse('[' + property.OccursInValuationPortfolios + ']' || '[]');
          const linkedToPortfolio = OccursInValuationPortfolios.filter((link) => {
            return link == item.Oid;
          });
          property.checkbox = linkedToPortfolio.length > 0;
        }

        this.checkAllChecked = true;
        this.selectedProperties = properties.filter((element) => {
          return element.checkbox;
        });

        this.propertyData = properties;
        this.availableUsers = users;
        this.availableRoles = roles;
        this.windowTitle = this._translate.instant('projectValuationCopyValuationPortfolioDialog.title');
        this.isDialogOpened = true;
      });
    });
  }

  /**
   * Confirm
   */
  confirm() {
    this.isCopyConfirmed = true;
    this.formData.controls.DivisionOfPortfolio.setValue(this.formData.controls.DivisionOfPortfolio.value == 'DivisionOfPortfolio' ? 1 : 0);
    this.formData.controls.Scenario.setValue(this.formData.controls.DivisionOfPortfolio.value == 'Scenario' ? 1 : 0);
    this.formData.addControl("SourceOid", new UntypedFormControl());
    this.formData.addControl("AssociatedPropertyIds", new UntypedFormControl());
    this.formData.controls.SourceOid.setValue(this.currentOid);
    this.selectedProperties = this.propertyData.filter((element) => {
      return element.checkbox;
    });
    this.formData.controls.AssociatedPropertyIds.setValue(this.selectedProperties.map(x => { return x.Oid }));

    this.save();
  }

  /**
   * Gets the data
   * @param item
   * @returns
   */
  private getData(item: any) {
    const filter = {
      logic: "and",
      filters: [{ field: "OccursInValuationPortfolios", operator: "containsInCsv", value: item.Oid }]
    };
    const valuationProperties = this.valEventPropertiesService.loadPagedList(1000, 0, 1, 1000, filter);
    const users = this.valEventRoleMgmtService.getUsers();
    const roles = this.valEventRoleMgmtService.getRoles();
    const roleAssignments = this.valEventRoleMgmtService.roleAssignments(item.Oid);
    const valuationPeriods = this.valEventCopyService.getValuationPeriods(this.copyValuationPortfolioData.ProjectID)

    return forkJoin([valuationProperties, users, roles, roleAssignments, valuationPeriods]);
  }

  /**
   * Next clicked
   */
  next() {
    if (this.page < 4) {
      this.page += 1;
    }
  }

  /**
   * Back clicked
   */
  back() {
    if (this.page > 1) {
      this.page -= 1;
    }
  }

  /**
   * Select/deselect all clicked
   */
  selectOrDeselectAllCheckbox() {
    for (const property of this.propertyData) {
      property.checkbox = this.checkAllChecked;
    }
  }
}

